// ============================================================
//  searchable-select.js
//  Dua varian Alpine.data untuk TomSelect + fetch dari server
//
//  Versi 1 — autoSelect      : dropdown list biasa
//  Versi 2 — autoSelectTable : dropdown bergaya tabel (kolom)
// ============================================================

const KNOWN_DATASET_KEYS = new Set([
  'fetchUrl', 'label', 'queryParam', 'labelKey', 'valueKey',
  'modelKey', 'placeholder', 'size', 'extraParams',
])

// ─────────────────────────────────────────────
// Helper bersama
// ─────────────────────────────────────────────

function _parseDataset(self) {
  const d = self.$el.dataset
  self.fetchUrl    = d.fetchUrl    || ''
  self.label       = d.label       || ''
  self.queryParam  = d.queryParam  || 'q'
  self.labelKey    = d.labelKey    || 'label'
  self.valueKey    = d.valueKey    || 'value'
  self.modelKey    = d.modelKey    || null
  self.placeholder = d.placeholder || 'Pilih data...'
  self.size        = d.size        || 'md'
  try {
    self.extraParams = d.extraParams ? JSON.parse(d.extraParams) : {}
  } catch (e) {
    console.warn('[autoSelect] Invalid extraParams JSON:', d.extraParams)
    self.extraParams = {}
  }
}

function _buildLoadFn(self) {
  return function (query, callback) {
    self.loading = true
    const params = new URLSearchParams(self.extraParams)
    params.append(self.queryParam, query)
    fetch(`${self.fetchUrl}?${params}`, {
      headers: { 'Accept': 'application/json' },
    })
      .then(res => res.json())
      .then(json => { callback(json); self.loading = false })
      .catch(() => { callback(); self.loading = false })
  }
}

function _onInitialize(self) {
  return function () {
    const map = { sm: 'ts-sm', md: 'ts-md', lg: 'ts-lg' }
    this.wrapper.classList.add(map[self.size] || 'ts-md')
  }
}

function _onChange(self) {
  return (value) => {
    if (self.modelKey) self.$dispatch('select-change', { key: self.modelKey, value })
    self.$dispatch('select-value', value)
  }
}

function _listenModalClose(self) {
  window.addEventListener('modal:close', (e) => {
    const parentModal = self.$el.closest('[data-modal-id]')
    if (parentModal?.dataset.modalId === e.detail?.id) self.reset()
  })
}

function _resetInstance(self) {
  if (self.instance) {
    self.instance.clear(true)
    self.instance.clearOptions()
    self.instance.setValue(null, true)
    self.instance.input.value = ''
    self.instance.control_input.value = ''
  }
  if (self.modelKey) {
    self.$dispatch('select-change', { key: self.modelKey, value: null })
  }
}

// ─────────────────────────────────────────────
// Versi 1 — autoSelect (dropdown list biasa)
// ─────────────────────────────────────────────

export function registerSearchableSelect(Alpine) {
  Alpine.data('autoSelect', () => ({

    // Core config
    fetchUrl: '',
    label: '',
    queryParam: 'q',
    labelKey: 'label',
    valueKey: 'value',
    modelKey: null,
    placeholder: 'Pilih data...',
    size: 'md',
    extraParams: {},
    instance: null,
    loading: false,

    init() {
      _parseDataset(this)
      this._initTomSelect()
      _listenModalClose(this)
    },

    _initTomSelect() {
      const self = this

      this.instance = new TomSelect(this.$refs.select, {
        valueField:  this.valueKey,
        labelField:  this.labelKey,
        searchField: this.labelKey,
        placeholder: this.placeholder,
        maxOptions:  50,
        dropdownParent: 'body',
        preload: 'focus',
        plugins: ['dropdown_input'],

        onDropdownOpen: function () {
          setTimeout(() => this.positionDropdown(), 50)
        },

        load:         _buildLoadFn(self),
        onInitialize: _onInitialize(self),
        onChange:     _onChange(self),
      })
    },

    reset() {
      _resetInstance(this)
    },
  }))
}

// ─────────────────────────────────────────────
// Versi 2 — autoSelectTable (dropdown bergaya tabel)
//
// data-columns JSON array, tiap item:
//   { key, label, width, mono? }
//
// Contoh:
//   data-columns='[
//     {"key":"code","label":"Kode","width":"20%","mono":true},
//     {"key":"name","label":"Nama","width":"50%"},
//     {"key":"category","label":"Kategori","width":"30%"}
//   ]'
// ─────────────────────────────────────────────

export function registerSearchableSelectTable(Alpine) {
  Alpine.data('autoSelectTable', () => ({

    // Core config (sama dengan autoSelect)
    fetchUrl: '',
    label: '',
    queryParam: 'q',
    labelKey: 'label',
    valueKey: 'value',
    modelKey: null,
    placeholder: 'Pilih data...',
    size: 'md',
    extraParams: {},

    // Config tambahan untuk tabel
    columns: [], // [{ key, label, width, mono? }]

    instance: null,
    loading: false,

    init() {
      _parseDataset(this)
      try {
        this.columns = this.$el.dataset.columns
          ? JSON.parse(this.$el.dataset.columns)
          : []
      } catch (e) {
        console.warn('[autoSelectTable] Invalid columns JSON:', this.$el.dataset.columns)
        this.columns = []
      }
      this._initTomSelect()
      _listenModalClose(this)
    },

    _initTomSelect() {
      const self = this
      const cols = this.columns

      this.instance = new TomSelect(this.$refs.select, {
        valueField:  this.valueKey,
        labelField:  this.labelKey,
        // Cari di semua kolom yang didefinisikan
        searchField: cols.length ? cols.map(c => c.key) : [this.labelKey],
        placeholder: this.placeholder,
        maxOptions:  50,
        dropdownParent: 'body',
        preload: 'focus',
        plugins: ['dropdown_input'],

        // ── Render option sebagai baris tabel ──
        render: {
          option(data, escape) {
            const cells = cols.map(c => {
              const cls = c.mono ? 'ts-tcell is-mono' : 'ts-tcell'
              return `<div class="${cls}" style="width:${c.width || 'auto'}; flex:${c.flex || '1 1 0'};min-width:0;">${escape(data[c.key] ?? '')}</div>`
            }).join('')
            return `<div class="ts-table-row">${cells}</div>`
          },

          // Item terpilih tetap menampilkan labelKey
          item(data, escape) {
            return `<div>${escape(data[self.labelKey] ?? '')}</div>`
          },
        },

        // ── Injeksi header kolom saat dropdown terbuka ──
        onDropdownOpen(dropdown) {
          // Tandai dropdown agar CSS spesifik tabel aktif
          dropdown.classList.add('ts-table')

          if (!dropdown.querySelector('.ts-table-header')) {
            const header = document.createElement('div')
            header.className = 'ts-table-header'

            cols.forEach(col => {
              const cell = document.createElement('div')
              cell.className = 'ts-header-cell'
              cell.style.width = col.width || 'auto'
              cell.style.flex = col.flex || 'unset'
              cell.textContent = col.label
              header.appendChild(cell)
            })

            // Sisipkan setelah input search (plugin dropdown_input),
            // sebelum .ts-dropdown-content
            const content = dropdown.querySelector('.ts-dropdown-content')
            if (content) dropdown.insertBefore(header, content)
            else dropdown.appendChild(header)
          }

          setTimeout(() => this.positionDropdown(), 50)
            // 🔥 Reposition saat scroll (capture: true agar tangkap semua scroll container)
            this._scrollHandler = () => this.positionDropdown()
            window.addEventListener('scroll', this._scrollHandler, true)
        },
onDropdownClose: function () {
  // 🔥 Bersihkan listener saat dropdown tutup
  if (this._scrollHandler) {
    window.removeEventListener('scroll', this._scrollHandler, true)
    this._scrollHandler = null
  }
},
        load:         _buildLoadFn(self),
        onInitialize: _onInitialize(self),
        onChange:     _onChange(self),
      })
    },

    reset() {
      _resetInstance(this)
    },
  }))
}

// ─────────────────────────────────────────────
// Registrasi keduanya sekaligus (opsional)
// ─────────────────────────────────────────────

export function registerAllSelectComponents(Alpine) {
  registerSearchableSelect(Alpine)
  registerSearchableSelectTable(Alpine)
}